import React from 'react';
import { Mountain, Compass, Heart, Sparkles } from 'lucide-react';

const VisionMission = () => {
  return (
    <section className="relative bg-ozone-white py-24 overflow-hidden" data-testid="vision-mission-section">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-kabi-teal/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-solar-kinetic/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="inline-block bg-kabi-teal/10 text-kabi-teal px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            Why We Exist
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-basalt-carbon mb-6 leading-tight">
            Big Insights, <span className="text-kabi-teal">Real Impact.</span>
          </h2>
          <p className="text-xl text-basalt-carbon/80 leading-relaxed">
            Driven by purpose and built for impact, Sustainify<sup>®</sup> is where global insights meet real-world change.
          </p>
        </div>

        {/* Vision & Mission Hero Block */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Vision Card */}
          <div 
            data-testid="vision-card"
            className="relative bg-gradient-to-br from-kabi-teal to-kabi-teal/80 text-ozone-white rounded-3xl p-10 md:p-12 overflow-hidden shadow-xl hover:shadow-2xl transition-all group"
            style={{
              backgroundImage: 'linear-gradient(135deg, rgba(45, 90, 39, 0.95), rgba(15, 46, 31, 0.95)), url(https://images.unsplash.com/photo-1469474968028-56623f02e42e)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-solar-kinetic/10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-700"></div>
            
            <div className="relative">
              <div className="flex items-center mb-6">
                <div className="bg-solar-kinetic text-basalt-carbon w-16 h-16 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                  <Mountain size={32} strokeWidth={2} />
                </div>
                <div>
                  <span className="text-solar-kinetic text-xs font-bold uppercase tracking-widest">Our Vision</span>
                  <h3 className="text-2xl font-bold mt-1">What We're Creating</h3>
                </div>
              </div>
              
              <p className="text-3xl md:text-4xl font-bold leading-tight mb-2">
                Inclusive and thriving
              </p>
              <p className="text-3xl md:text-4xl font-bold leading-tight">
                <span className="text-solar-kinetic">futures</span> for people and planet.
              </p>
            </div>
          </div>

          {/* Mission Card */}
          <div 
            data-testid="mission-card"
            className="relative bg-ozone-white border-2 border-kabi-teal/20 rounded-3xl p-10 md:p-12 hover:border-kabi-teal hover:shadow-xl transition-all group overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-kabi-teal/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-700"></div>
            
            <div className="relative">
              <div className="flex items-center mb-6">
                <div className="bg-kabi-teal text-ozone-white w-16 h-16 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                  <Compass size={32} strokeWidth={2} />
                </div>
                <div>
                  <span className="text-kabi-teal text-xs font-bold uppercase tracking-widest">Our Mission</span>
                  <h3 className="text-2xl font-bold mt-1 text-basalt-carbon">How We Get There</h3>
                </div>
              </div>
              
              <p className="text-3xl md:text-4xl text-basalt-carbon leading-tight font-bold">
                through strategic advisory, change management, and projects that deliver a <span className="bg-solar-kinetic text-basalt-carbon px-2 rounded">just transition.</span>
              </p>
            </div>
          </div>
        </div>

        {/* Values Banner */}
        <div 
          data-testid="values-banner"
          className="relative bg-basalt-carbon rounded-3xl p-10 md:p-12 overflow-hidden shadow-xl"
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-solar-kinetic/5 rounded-full blur-3xl"></div>
          
          <div className="relative grid md:grid-cols-[auto_1fr] gap-8 items-center">
            <div className="bg-solar-kinetic text-basalt-carbon w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
              <Sparkles size={40} strokeWidth={2} />
            </div>
            
            <div>
              <span className="text-solar-kinetic text-xs font-bold uppercase tracking-widest">Our Values</span>
              <div className="flex flex-wrap gap-3 mt-3 mb-4">
                <span className="bg-solar-kinetic text-basalt-carbon px-4 py-1 rounded-full text-lg font-bold">Integrity</span>
                <span className="bg-solar-kinetic text-basalt-carbon px-4 py-1 rounded-full text-lg font-bold">Impact</span>
                <span className="bg-solar-kinetic text-basalt-carbon px-4 py-1 rounded-full text-lg font-bold">Innovation</span>
              </div>
              <p className="text-lg text-ozone-white/90 leading-relaxed">
                We're <span className="text-solar-kinetic font-semibold">future-minded</span> and <span className="text-solar-kinetic font-semibold">mission-centered</span>, pursuing outcomes that create a future with people and planet at the heart.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisionMission;
